import { useOnValueChange, usePrevious } from '@calendly/hooks'
import { useEffect, useMemo, useState } from 'react'

import { useDebounce } from 'hooks/useDebounce'

const DEFAULT_ITEMS_PER_PAGE = 10

const saveItemsFormIndex = (items) => items.map((item, index) => ({ ...item, formIndex: index }))

export const usePaginatedSearch = ({
  items = [],
  filterFn = null,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  skip = false
}) => {
  const isPaginationNeeded = !skip && items.length > itemsPerPage

  const itemsWithPosition = useMemo(() => (
    isPaginationNeeded ? saveItemsFormIndex(items) : items
  ), [items, isPaginationNeeded])

  const [filteredItems, setFilteredItems] = useState(itemsWithPosition)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebounce(searchQuery, 300)
  const previousQuery = usePrevious(debouncedQuery)

  useOnValueChange(itemsWithPosition, () => {
    if (!isPaginationNeeded) return

    setFilteredItems((filterFn && debouncedQuery !== '')
      ? filterFn(itemsWithPosition, debouncedQuery)
      : itemsWithPosition
    )
  })

  useEffect(() => {
    if (!isPaginationNeeded) return

    if (filterFn && debouncedQuery !== previousQuery) {
      setFilteredItems(filterFn(itemsWithPosition, debouncedQuery))
      setCurrentPage(1)
    }
  }, [isPaginationNeeded, filterFn, debouncedQuery, previousQuery, itemsWithPosition])

  if (!isPaginationNeeded) {
    return {
      pageItems: items,
      showSearch: false,
      showSearchEmptyResult: false,
      showPagination: false
    }
  }

  const startIndex = (currentPage - 1) * itemsPerPage
  const nextPageIndex = startIndex + itemsPerPage
  const pageItems = filteredItems.slice(startIndex, nextPageIndex)

  const openNextPage = nextPageIndex < filteredItems.length
    ? () => setCurrentPage((page) => (page + 1))
    : null

  const openPrevPage = currentPage > 1
    ? () => setCurrentPage((page) => (page - 1))
    : null

  const showSearch = isPaginationNeeded
  const showSearchEmptyResult = isPaginationNeeded && pageItems.length === 0
  const showPagination = isPaginationNeeded && pageItems.length > 0 && pageItems.length !== itemsWithPosition.length

  return {
    pageItems,

    showSearch,
    showSearchEmptyResult,
    showPagination,

    searchQuery,
    setSearchQuery,

    currentPage,
    openNextPage,
    openPrevPage,
    pageSize: itemsPerPage,
    totalCount: filteredItems.length
  }
}
